// app/components/chat/ProjectRulesModal.tsx
import { useEffect, useState } from 'react';
import { rulesStore, setProjectRules } from '~/lib/stores/rules';
import { useStore } from '@nanostores/react';
import { Modal } from '~/components/ui/Modal';
import { chatId } from '~/lib/persistence/useChatHistory';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/remix';
import { userStore } from '~/lib/stores/user';

interface ProjectRulesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ProjectRulesModal({ isOpen, onClose }: ProjectRulesModalProps) {
  const { email } = userStore.get();
  const { projectRules, loaded } = useStore(rulesStore);
  const [rules, setRules] = useState(projectRules || '');
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const currentChatId = useStore(chatId);

  useEffect(() => {
    if (projectRules && loaded) {
      setRules(projectRules);
    }
  }, [projectRules, loaded]);

  useEffect(() => {
    if (!isOpen) {
      setSaveStatus('idle');
    }
  }, [isOpen]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveStatus('saving');

      console.log(
        '[ProjectRulesModal] Saving rules:',
        currentChatId ? `for chat: ${currentChatId}` : 'as temporary rules in localStorage',
      );

      // Use the optimized setProjectRules which returns success status
      const success = await setProjectRules(rules);

      if (success) {
        setSaveStatus('success');
        console.log('[ProjectRulesModal] Rules saved successfully');

        setTimeout(() => {
          onClose();
          setSaveStatus('idle');
        }, 1000);
      } else {
        setSaveStatus('error');
        toast.error('Failed to save rules. Please try again.');
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          message: 'Error saving project rules',
          rules,
          chatId: currentChatId,
        },
      });
      console.error('[ProjectRulesModal] Error saving rules:', error);
      setSaveStatus('error');
      toast.error('Failed to save rules. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="bg-alpha-background-secondary rounded-xl border border-alpha-border-primary p-6 w-full max-w-[500px]">
        <h2 className="text-[16px] text-alpha-text-white font-semibold mb-1">Project Rules</h2>

        <div className="h-px bg-[#414142] my-3 -mx-6" />

        <div className="mb-4">
          <p className="text-[12px] text-alpha-text-heading-start mb-2">
            {currentChatId
              ? 'Set project-specific rules for this chat. These rules will be applied to all prompts in this chat only. Leave empty to remove them.'
              : 'Define project rules before starting a chat. These rules will be temporarily stored and applied once you start a chat. If you wish to remove them, simply delete the content.'}
          </p>

          {!currentChatId && (
            <div className="text-xs text-gray-300 bg-gray-400/10 p-3 rounded-lg mb-2 border border-yellow-400/20 flex">
              <div className="flex items-center">
                <div className="i-ph:info text-xs mr-1 w-8 h-8"></div>
                <span className="leading-relaxed">
                  Using Rules, you can set specific guidelines for your project. These rules will be applied to all
                  prompts in this chat only. Learn more about{' '}
                  <a
                    href="https://alphadocs.dualite.dev/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 underline hover:text-blue-600"
                  >
                    Project Rules
                  </a>
                </span>
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-col space-y-3">
          <textarea
            value={rules}
            onChange={(e) => setRules(e.target.value)}
            className="w-full h-64 p-3 bg-[#1D1D1D] text-[#B5B5B6] border border-[#414142] rounded-lg placeholder-[#929292] text-sm focus:outline-none focus:border-[#2C4DF1]"
            placeholder="Enter your project-specific rules here..."
            disabled={saving}
          />
          <div className="flex justify-end space-x-2 mt-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-[#B5B5B6] hover:text-alpha-text-white transition-colors"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className={`flex items-center justify-center gap-[6px] py-[10px] px-4 rounded-lg text-white text-[13px] font-medium transition-all duration-200 ease disabled:opacity-50 disabled:cursor-not-allowed ${
                saveStatus === 'success'
                  ? 'bg-green-600'
                  : saveStatus === 'error'
                    ? 'bg-red-600'
                    : 'bg-gradient-to-b from-alpha-primary-start to-alpha-primary-end hover:brightness-94'
              }`}
            >
              {saving ? (
                <>
                  <div className="i-svg-spinners:90-ring-with-bg h-4 w-4"></div>
                  <span>Saving...</span>
                </>
              ) : saveStatus === 'success' ? (
                <>
                  <div className="i-ph:check-bold h-4 w-4"></div>
                  <span>Saved!</span>
                </>
              ) : saveStatus === 'error' ? (
                <>
                  <div className="i-ph:x-bold h-4 w-4"></div>
                  <span>Error</span>
                </>
              ) : (
                <span>Save Rules</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
